import { useState, type FormEvent } from "react";
import { useLocation, useNavigate, type Location } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { ApiError } from "../api/client";
import { login2FA } from "../api/auth";

// Forwarded from LoginPage (which itself got it from RequireAuth) so a
// signed-out visit to a deep link like /delete-account survives the 2FA
// hop and still lands there, not on "/". See LoginPage's matching type.
interface TwoFactorLocationState {
  from?: Pick<Location, "pathname" | "search">;
}

export function TwoFactorChallengePage() {
  const { api, setSession } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const from = (location.state as TwoFactorLocationState | null)?.from;
  const target = from ? `${from.pathname}${from.search}` : "/";
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pending = (() => {
    try {
      const raw = sessionStorage.getItem("prism-temp-2fa");
      return raw ? (JSON.parse(raw) as { temp_token: string; email: string }) : null;
    } catch {
      return null;
    }
  })();

  if (!pending) {
    nav("/login", { replace: true, state: { from } });
    return null;
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!pending) return;
    setBusy(true);
    setError(null);
    try {
      const res = await login2FA(api, pending.temp_token, code);
      sessionStorage.removeItem("prism-temp-2fa");
      setSession({ access_token: res.access_token, refresh_token: res.refresh_token, account: res.account });
      nav(target, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "verification failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card center-card">
      <h1>Two-factor code</h1>
      <p className="meta">Enter the 6-digit code from your authenticator, or a recovery code.</p>
      <form onSubmit={submit}>
        <div className="field">
          <label htmlFor="code">Code</label>
          <input id="code" type="text" value={code} autoFocus required
            inputMode="text" autoComplete="one-time-code"
            onChange={(e) => setCode(e.target.value)} />
        </div>
        {error && <div className="error">{error}</div>}
        <button className="btn primary" type="submit" disabled={busy}>
          {busy ? "Verifying…" : "Verify"}
        </button>
      </form>
    </div>
  );
}
